import { useState, useEffect, ChangeEvent, ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Stack } from '@mui/material';
import { onboardingSubmit, getServiceTypes } from '../services/onboardingService';
import { getAreas } from '../services/profileService';
import { supabase } from '../services/supabase';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingSpinner from '@components/LoadingSpinner';
import { TestingButtons } from '../utils/authTesting.jsx';

import GoogleMapsLinks from '@components/common/GoogleMapsLinks';
import AreaSuggestion from '@components/common/AreaSuggestion';
import type { AcceptSuggestionPayload } from '@components/common/AreaSuggestion';
import { matchPlaceToAreaWithAutoSelect } from '../utils/areaMatchingUtils';
import type { MatchWithAutoSelectResult } from '../utils/areaMatchingUtils';

// Import shared model types
import type { ServiceType } from '@app-types/models/plan';
import type { AreaRegion, AreaLocation } from '@app-types/models/area';

// Import child component prop types
import type { PlaceSelectedResult } from '../components/onboarding/Step1Form';

// Import components
import OnboardingHeader from '../components/onboarding/OnboardingHeader';
import Step1Form from '../components/onboarding/Step1Form';
import Step2Form from '../components/onboarding/Step2Form';
import ThankYouMessage from '../components/onboarding/ThankYouMessage';
import TermsDialog from '../components/onboarding/TermsDialog';
import PrivacyPolicyDialog from '../components/onboarding/PrivacyPolicyDialog';

interface OnboardingFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  businessName: string;
  address: string;
  br: File[];
  storePhoto: File[];
  proof: File[];
  additional: string;
  selectedServiceTypes: string[];
  addedServiceTypes: Array<{ _id: string; [key: string]: unknown }>;
  referredByCode: string;
  socialMediaLink: string;
  coordinates: number[];
  placeId: string;
  region: string;
  businessArea: string;
  [key: string]: unknown;
}

interface SelectedFilesState {
  br: File[];
  storePhoto: File[];
  proof: File[];
  [key: string]: File[];
}

interface AreaSuggestionStrings {
  autoSelected?: string;
  accepted?: string;
}

interface TranslationStrings {
  title: string;
  thankYou: string;
  verification: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  businessName: string;
  address: string;
  region: string;
  businessArea: string;
  serviceTypes: string;
  additional: string;
  referredByCode: string;
  br: string;
  storePhoto: string;
  proof: string;
  uploadImages: string;
  socialMediaLink: string;
  submit: string;
  error: string;
  iAccept: string;
  close: string;
  termsAndConditions: string;
  privacyPolicy: string;
  createAccount: string;
  createAccountMessage: string;
  checkEmailMessage: string;
  resendEmail: string;
  resendEmailWithTimer: string;
  failedToSendVerificationCode: string;
  registrationComplete: string;
  [key: string]: string;
}

interface TranslationsMapEntry {
  t: TranslationStrings;
  areaSuggestion: AreaSuggestionStrings;
}

interface TranslationsMap {
  zh: TranslationsMapEntry;
  en: TranslationsMapEntry;
  [key: string]: TranslationsMapEntry;
}

const OnboardingPage: React.FC = () => {
  const location = useLocation();
  const [currentLanguage, setCurrentLanguage] = useState<string>('zh');
  const { control, handleSubmit, setValue, getValues, trigger, formState: { errors } } = useForm<OnboardingFormValues>({
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      companyName: '',
      businessName: '',
      address: '',
      br: [],
      storePhoto: [],
      proof: [],
      additional: '',
      selectedServiceTypes: [],
      addedServiceTypes: [],
      referredByCode: '',
      socialMediaLink: '',
      coordinates: [],
      placeId: '',
      region: '',
      businessArea: ''
    }
  });

  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const error = '';
  const [selectedFiles, setSelectedFiles] = useState<SelectedFilesState>({
    br: [],
    storePhoto: [],
    proof: []
  });
  const [isTncAccepted, setIsTncAccepted] = useState<boolean>(false);
  const [isTncDialogOpen, setIsTncDialogOpen] = useState<boolean>(false);
  const [isPrivacyPolicyAccepted, setIsPrivacyPolicyAccepted] = useState<boolean>(false);
  const [isPrivacyPolicyDialogOpen, setIsPrivacyPolicyDialogOpen] = useState<boolean>(false);
  const [showAccountForm, setShowAccountForm] = useState<boolean>(false);
  const [currentPlaceId, setCurrentPlaceId] = useState<string>('');
  const [currentAddress, setCurrentAddress] = useState<string>('');
  const timestamp = Date.now();

  // Area matching state
  const [areaData, setAreaData] = useState<AreaRegion[]>([]);
  const [areaSuggestion, setAreaSuggestion] = useState<MatchWithAutoSelectResult | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [availableLocations, setAvailableLocations] = useState<AreaLocation[]>([]);

  const translations: TranslationsMap = {
    zh: {
      t: {
        title: 'HKStylist 商戶申請',
        thankYou: '感謝您的申請！',
        verification: '您的驗證郵件已經寄出，請驗證你的郵件。申請正在處理中，我們會在三個工作日內聯絡您。',
        email: '電郵地址',
        password: '密碼',
        confirmPassword: '確認密碼',
        companyName: '公司名稱',
        businessName: '商業名稱',
        address: '商戶地址',
        region: '地區',
        businessArea: '區域',
        serviceTypes: '服務類型',
        additional: '其他補充 (幫助我們了解商戶)',
        referredByCode: '推薦碼 (可選)',
        br: '商業登記 BR (上載圖片)',
        storePhoto: '門店相片 (上載圖片)',
        proof: '業務證明 (單據,銀行月結單等) (上載圖片)',
        uploadImages: '上載圖片',
        socialMediaLink: '社交媒體連結',
        submit: '提交',
        error: '錯誤: ',
        iAccept: '我接受',
        close: '關閉',
        termsAndConditions: '條款及細則',
        privacyPolicy: '隱私政策',
        createAccount: '建立帳戶',
        createAccountMessage: '請填寫以下資料以建立帳戶',
        checkEmailMessage: '請檢查您的電郵以驗證帳戶。',
        resendEmail: '重發驗證電郵',
        resendEmailWithTimer: '重發驗證電郵 (%s秒)',
        failedToSendVerificationCode: '發送驗證電郵失敗',
        registrationComplete: '註冊已完成！請檢查您的電郵以驗證帳戶。我們會在三個工作日內審核您的申請。'
      },
      areaSuggestion: {
        autoSelected: undefined,
        accepted: undefined
      }
    },
    en: {
      t: {
        title: 'HKStylist Merchant Application',
        thankYou: 'Thank you for your application!',
        verification: 'Your verification email has been sent. Please verify your email. Your application is being processed, and we will contact you within three business days.',
        email: 'Email Address',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        companyName: 'Company Name',
        businessName: 'Business Name',
        address: 'Business Address',
        region: 'Region',
        businessArea: 'Business Area',
        serviceTypes: 'Service Types',
        additional: 'Additional Information (Help us understand your business)',
        referredByCode: 'Referral Code (Optional)',
        br: 'Business Registration BR (Upload Images)',
        storePhoto: 'Store Photos (Upload Images)',
        proof: 'Proof of Business (Invoices, Bank Statements, etc.) (Upload Images)',
        uploadImages: 'Upload Images',
        socialMediaLink: 'Social Media Link',
        submit: 'Submit',
        error: 'Error: ',
        iAccept: 'I accept the',
        close: 'Close',
        termsAndConditions: 'Terms and Conditions',
        privacyPolicy: 'Privacy Policy',
        createAccount: 'Create Account',
        createAccountMessage: 'Please fill in the following information to create your account',
        checkEmailMessage: 'Please check your email to verify your account.',
        resendEmail: 'Resend Verification Email',
        resendEmailWithTimer: 'Resend Email (%ss)',
        failedToSendVerificationCode: 'Failed to send verification code',
        registrationComplete: 'Registration complete! Please check your email to verify your account. We will review your application within a few working days.'
      },
      areaSuggestion: {
        autoSelected: undefined,
        accepted: undefined
      }
    }
  };

  const t = translations[currentLanguage].t;
  const areaSuggestionStrings = translations[currentLanguage].areaSuggestion;

  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        const typesResponse = await getServiceTypes();
        setServiceTypes(typesResponse.serviceTypes);
      } catch {
        toast.error(t.error + 'Failed to load service types');
      }
    };

    const fetchAreaData = async () => {
      try {
        const areasResponse = await getAreas();
        setAreaData(areasResponse);
      } catch (error) {
        console.error('Error fetching area data:', error);
      }
    };

    fetchServiceTypes();
    fetchAreaData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]);

  useEffect(() => {
    if (selectedRegion) {
        const region = areaData.find((r) => r._id === selectedRegion);
        setAvailableLocations(region ? region.locations : []);
    }
  }, [selectedRegion, areaData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referralCode = params.get('referral_code');
    if (referralCode) {
      setValue('referredByCode', referralCode);
    }
  }, [location.search, setValue]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, files } = event.target;
    if (!files) return;
    const fileArray = Array.from(files);
    type FileFieldName = 'br' | 'storePhoto' | 'proof';
    setValue(name as FileFieldName, fileArray);
    setSelectedFiles(prevState => ({
      ...prevState,
      [name]: [...(prevState[name] || []), ...fileArray]
    }));
  };

  const handleFileDelete = (name: string, index: number) => {
    setSelectedFiles(prevState => {
      const updatedFiles = [...prevState[name]];
      updatedFiles.splice(index, 1);
      type FileFieldName = 'br' | 'storePhoto' | 'proof';
      setValue(name as FileFieldName, updatedFiles);
      return {
        ...prevState,
        [name]: updatedFiles
      };
    });
  };

  const handleTncDialogOpen = () => {
    setIsTncDialogOpen(true);
  };

  const handleTncDialogClose = () => {
    setIsTncDialogOpen(false);
  };

  const handlePrivacyPolicyDialogOpen = () => {
    setIsPrivacyPolicyDialogOpen(true);
  };

  const handlePrivacyPolicyDialogClose = () => {
    setIsPrivacyPolicyDialogOpen(false);
  };

  const handleRegionChange = (event: { target: { value: string } }) => {
    const regionId = event.target.value;
    setSelectedRegion(regionId);
    const region = areaData.find((r) => r._id === regionId);
    setAvailableLocations(region ? region.locations : []);
    setValue('region', regionId);
    setValue('businessArea', '');
  };

  const handlePlaceSelected = (placeData: PlaceSelectedResult) => {
    console.log('[OnboardingPage] onPlaceSelected called with:', placeData);
    // placeData structure: { address, coordinates, place_id }
    // coordinates is an array [lng, lat] for GeoJSON format
    if (placeData.coordinates && Array.isArray(placeData.coordinates)) {
      setValue('coordinates', placeData.coordinates);
    }
    // Store the place_id for generating Google Maps links
    if (placeData.place_id) {
      setValue('placeId', placeData.place_id);
      setCurrentPlaceId(placeData.place_id);
    }
    // Store the address for display
    setCurrentAddress(placeData.address);

    // Perform area matching
    if (areaData.length > 0) {
      const areaMatchResult = matchPlaceToAreaWithAutoSelect(placeData, areaData);
      console.log('[OnboardingPage] Area match result:', areaMatchResult);

      if (areaMatchResult.shouldAutoSelect && areaMatchResult.match) {
        const { match } = areaMatchResult;
        console.log('[OnboardingPage] Auto-selecting area:', match);
        setSelectedRegion(match.region_id);
        setValue('region', match.region_id, { shouldValidate: true });
        setValue('businessArea', match.location_id, { shouldValidate: true });
        toast.success(areaSuggestionStrings?.autoSelected || `Area auto-selected: ${match.chinese || match.english} (${areaMatchResult.confidence}% confidence)`);
      } else if (areaMatchResult.shouldShowSuggestion) {
        setAreaSuggestion(areaMatchResult);
      }
    }
  };

  const handleAreaSuggestionAccept = (payload: AcceptSuggestionPayload) => {
    console.log('[OnboardingPage] Accepting area suggestion:', payload);
    const { regionId, locationId, match } = payload;
    setSelectedRegion(regionId);
    setValue('region', regionId, { shouldValidate: true });
    setValue('businessArea', locationId, { shouldValidate: true });
    setAreaSuggestion(null);
    toast.success(areaSuggestionStrings?.accepted || `Area suggestion accepted: ${match.chinese || match.english}`);
  };

  const handleAreaSuggestionDismiss = () => {
    setAreaSuggestion(null);
  };

  const handleNextStep = async () => {
    const isValid = await trigger([
      'companyName',
      'businessName',
      'address',
      'socialMediaLink',
      'selectedServiceTypes'
    ]);

    if (!isValid) {
      toast.error('Please fill in all required fields correctly before proceeding.');
      return;
    }

    // Check if coordinates and placeId are available (required for address autocomplete)
    const formValues = getValues();
    if (!formValues.coordinates || !Array.isArray(formValues.coordinates) || formValues.coordinates.length !== 2) {
      toast.error(currentLanguage === 'zh' ? '請從自動完成建議中選擇有效地址' : 'Please select a valid address from the autocomplete suggestions.');
      return;
    }

    if (!formValues.placeId) {
      toast.error(currentLanguage === 'zh' ? '請從自動完成建議中選擇有效地址' : 'Please select a valid address from the autocomplete suggestions.');
      return;
    }

    // Check if service types are selected (required)
    if (!formValues.selectedServiceTypes || !Array.isArray(formValues.selectedServiceTypes) || formValues.selectedServiceTypes.length === 0) {
      toast.error(currentLanguage === 'zh' ? '請選擇至少一種服務類型' : 'Please select at least one service type.');
      return;
    }

    if (!isTncAccepted) {
      toast.error('You must accept the terms and conditions to proceed.');
      return;
    }

    if (!isPrivacyPolicyAccepted) {
      toast.error('You must accept the privacy policy to proceed.');
      return;
    }

    setShowAccountForm(true);
  };

  const sendEmailVerification = async () => {
    setIsLoading(true);
    try {
      const formData = getValues();

      if (!formData.email || !formData.password || formData.password !== formData.confirmPassword) {
        toast.error('Please enter a valid email and ensure passwords match');
        return;
      }

      // Sign up the user
      const { error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/email-success`,
        }
      });

      if (signUpError) throw signUpError;

      // Immediately sign in after signup
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (signInError) throw signInError;

      // Save business data to database immediately after account creation
      // Use the existing onSubmit function to handle the form submission
      await onSubmit(formData);

      // Set email sent state after successful submission
      setIsEmailSent(true);
      toast.success('Account created and business registered successfully. Please verify your email.');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t.failedToSendVerificationCode;
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderSelectedFiles = (files: File[], name: string): ReactNode => (
    <Box display="flex" flexDirection="column" mt={2}>
      {files.map((file, index) => (
        <Box key={index} mt={1} display="flex" alignItems="center">
          <img
            src={URL.createObjectURL(file)}
            alt={file.name}
            style={{ width: '100px', height: 'auto', marginRight: '10px' }}
          />
          <button
            onClick={() => handleFileDelete(name, index)}
            style={{ marginLeft: '10px' }}
          >
            Delete
          </button>
        </Box>
      ))}
    </Box>
  );

  const onSubmit = async (formData: OnboardingFormValues) => {
    try {
      console.log('[OnboardingPage] Form data before FormData creation:', formData);
      console.log('[OnboardingPage] Coordinates:', formData.coordinates);
      console.log('[OnboardingPage] PlaceId:', formData.placeId);

      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'addedServiceTypes') {
          data.append(key, JSON.stringify((formData[key] as Array<{ _id: string }>).map(type => type._id)));
        } else if (key === 'coordinates' && Array.isArray(formData[key])) {
          // Handle coordinates as JSON string for FormData
          data.append(key, JSON.stringify(formData[key]));
          console.log('[OnboardingPage] Added coordinates to FormData:', JSON.stringify(formData[key]));
        } else if (formData[key] instanceof Array && key !== 'coordinates') {
          (formData[key] as File[]).forEach(file => {
            data.append(key, file);
          });
        } else {
          data.append(key, formData[key] as string);
        }
      });
      data.append('languageCode', currentLanguage);

      // Log FormData contents for debugging
      console.log('[OnboardingPage] FormData contents:');
      for (const [key, value] of data.entries()) {
        console.log(`${key}:`, value);
      }

      const result = await onboardingSubmit(data) as { success?: boolean; error?: string } | undefined;
      if (!result?.success) {
        toast.error(result?.error || 'An error occurred during submission');
        return;
      }

      setIsSubmitted(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred during submission';
      toast.error(errorMessage);
      console.error('Error submitting the form:', error);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box className="onboarding-container" {...(isTncDialogOpen ? { inert: '' } as Record<string, unknown> : {})}>
      <OnboardingHeader
        title={t.title}
        currentLanguage={currentLanguage}
        changeLanguage={setCurrentLanguage}
      />
      <Stack direction="column" spacing={2} sx={{ maxWidth: 600, mx: 'auto', padding: 4 }}>
        <Box className="main">
          {isSubmitted ? (
            <ThankYouMessage t={t} />
          ) : (
            <>
              <TestingButtons />
              {showAccountForm ? (
                <Step2Form
                  control={control}
                  errors={errors}
                  handleSubmit={handleSubmit}
                  onSubmit={onSubmit}
                  isLoading={isLoading}
                  isEmailSent={isEmailSent}
                  sendEmailVerification={sendEmailVerification}
                  error={error}
                  t={t}
                />
              ) : (
                <Step1Form
                  control={control}
                  errors={errors}
                  serviceTypes={serviceTypes}
                  selectedFiles={selectedFiles}
                  handleFileChange={handleFileChange}
                  renderSelectedFiles={renderSelectedFiles}
                  setValue={setValue}
                  isTncAccepted={isTncAccepted}
                  setIsTncAccepted={setIsTncAccepted}
                  isPrivacyPolicyAccepted={isPrivacyPolicyAccepted}
                  setIsPrivacyPolicyAccepted={setIsPrivacyPolicyAccepted}
                  handleTncDialogOpen={handleTncDialogOpen}
                  handlePrivacyPolicyDialogOpen={handlePrivacyPolicyDialogOpen}
                  onPlaceSelected={handlePlaceSelected}
                  handleNextStep={handleNextStep}
                  currentPlaceId={currentPlaceId}
                  currentAddress={currentAddress}
                  GoogleMapsLinks={GoogleMapsLinks}
                  areaSuggestion={areaSuggestion}
                  onAreaSuggestionAccept={handleAreaSuggestionAccept}
                  onAreaSuggestionDismiss={handleAreaSuggestionDismiss}
                  AreaSuggestion={AreaSuggestion}
                  error={error}
                  t={t}
                  areaData={areaData}
                  availableLocations={availableLocations}
                  selectedRegion={selectedRegion}
                  handleRegionChange={handleRegionChange}
                  currentLanguage={currentLanguage}
                />
              )}
            </>
          )}
        </Box>
      </Stack>

      <TermsDialog
        isOpen={isTncDialogOpen}
        onClose={handleTncDialogClose}
        currentLanguage={currentLanguage}
        timestamp={timestamp}
        t={t}
      />

      <PrivacyPolicyDialog
        isOpen={isPrivacyPolicyDialogOpen}
        onClose={handlePrivacyPolicyDialogClose}
        currentLanguage={currentLanguage}
        timestamp={timestamp}
        t={t}
      />
    </Box>
  );
};

export default OnboardingPage;
